// Bounded fan-out for per-file requests. Over HTTP/2 the browser no longer caps
// a page at 6 connections, so a gallery that starts one GET per thumbnail all
// at once can hold every server Redis connection at the same moment (the
// 2026-10-06 Lilach incident: ~127 body thumbnails in one burst). This runs
// `worker` over `items` with at most `limit` calls in flight.
//
// Results come back in input order, in Promise.allSettled shape
// ({ status: "fulfilled", value } | { status: "rejected", reason }), so one
// failing item never rejects the whole batch.
//
// Tuned together with the web's "bulk" request lane (REQUEST_LANE_BULK_MAX,
// default 6 concurrent /image, /api/bodies and /api/images/batch requests per
// web process, waiting up to REQUEST_LANE_BULK_WAIT_S = 10 s for a slot before
// a JSON 503 "busy"). At 6, one guest's downloads can fill one process's lane
// for a moment; other requests then queue rather than fail. Lowering either
// number without the other shifts that balance.
export const DEFAULT_FETCH_LIMIT = 6;

// The reason given to items that never started because the run was stopped.
const stoppedError = () => {
  const error = new Error("fetchWithLimit stopped before this item started");
  error.name = "AbortError";
  return error;
};

// `options.signal` (an AbortSignal) stops the run: once it is aborted no lane
// starts another item (a page that unmounted must not start new requests).
// The worker receives the signal as its third argument so it can abort its own
// request; items that never started are reported as rejected AbortErrors.
export const fetchWithLimit = async (
  items,
  worker,
  limit = DEFAULT_FETCH_LIMIT,
  { signal } = {}
) => {
  const list = Array.from(items || []);
  const results = new Array(list.length);
  const lanes = Math.min(list.length, Math.max(1, Math.floor(limit) || 1));
  const stopped = () => Boolean(signal && signal.aborted);
  let next = 0;

  // Each lane takes the next unclaimed item as soon as its previous one
  // settled, so the number in flight never exceeds `lanes`.
  const runLane = async () => {
    while (next < list.length && !stopped()) {
      const index = next;
      next += 1;
      try {
        results[index] = {
          status: "fulfilled",
          value: await worker(list[index], index, signal),
        };
      } catch (reason) {
        results[index] = { status: "rejected", reason };
      }
    }
  };

  await Promise.all(Array.from({ length: lanes }, runLane));
  for (let index = 0; index < list.length; index += 1) {
    if (!results[index]) {
      results[index] = { status: "rejected", reason: stoppedError() };
    }
  }
  return results;
};
